
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MapPin, User, Users } from 'lucide-react';

interface StateData {
  state: string;
  total: number;
  approved: number;
  pending: number;
}

interface GenderData {
  gender: string;
  count: number;
  percentage: number;
}

interface AgeGroupData {
  age_group: string;
  count: number;
}

const COLORS = ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#EF4444', '#10B981'];

const MemberAnalyticsSection = () => {
  const [stateData, setStateData] = useState<StateData[]>([]);
  const [genderData, setGenderData] = useState<GenderData[]>([]);
  const [ageGroupData, setAgeGroupData] = useState<AgeGroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalMembers, setTotalMembers] = useState(0);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch all members
      const { data: members, error } = await supabase
        .from('community_members')
        .select('state, gender, age_group, approved');

      if (error) throw error;

      const membersList = members || [];
      setTotalMembers(membersList.length);

      // Process state data
      const stateStats: Record<string, { total: number; approved: number; pending: number }> = {};
      
      membersList.forEach(member => {
        if (!stateStats[member.state]) {
          stateStats[member.state] = { total: 0, approved: 0, pending: 0 };
        }
        stateStats[member.state].total++;
        if (member.approved) {
          stateStats[member.state].approved++;
        } else {
          stateStats[member.state].pending++;
        }
      });

      const stateArray = Object.entries(stateStats)
        .map(([state, stats]) => ({
          state,
          ...stats
        }))
        .sort((a, b) => b.total - a.total);

      setStateData(stateArray);

      // Process gender data
      const genderStats: Record<string, number> = {};
      membersList.forEach(member => {
        if (member.gender) {
          genderStats[member.gender] = (genderStats[member.gender] || 0) + 1;
        }
      });

      const genderArray = Object.entries(genderStats)
        .map(([gender, count]) => ({
          gender: gender || 'Not specified',
          count,
          percentage: Math.round((count / membersList.length) * 100)
        }))
        .sort((a, b) => b.count - a.count);

      setGenderData(genderArray);

      // Process age group data
      const ageStats: Record<string, number> = {};
      membersList.forEach(member => {
        if (member.age_group) {
          ageStats[member.age_group] = (ageStats[member.age_group] || 0) + 1;
        }
      });

      const ageArray = Object.entries(ageStats)
        .map(([age_group, count]) => ({
          age_group: age_group || 'Not specified',
          count
        }))
        .sort((a, b) => b.count - a.count);

      setAgeGroupData(ageArray);

    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="py-8">
              <div className="flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-omoduduwa-indigo border-t-transparent rounded-full animate-spin"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total States</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stateData.length}</div>
            <p className="text-xs text-muted-foreground">
              Across Nigeria
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gender Distribution</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{genderData.length}</div>
            <p className="text-xs text-muted-foreground">
              Gender categories
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Age Groups</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ageGroupData.length}</div>
            <p className="text-xs text-muted-foreground">
              Age categories
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* State Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Members by State</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={stateData.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="state" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={12}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="approved" stackId="a" fill="#10B981" name="Approved" />
                <Bar dataKey="pending" stackId="a" fill="#F59E0B" name="Pending" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gender Distribution Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Gender Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={genderData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ gender, percentage }) => `${gender}: ${percentage}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Age Group Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Age Group Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageGroupData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age_group" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#4F46E5" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* State Details Table */}
        <Card>
          <CardHeader>
            <CardTitle>State Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-80 overflow-y-auto">
              <div className="space-y-2">
                {stateData.map((state) => (
                  <div key={state.state} className="flex justify-between items-center p-2 border rounded">
                    <div className="font-medium">{state.state}</div>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">✓ {state.approved}</span>
                      <span className="text-yellow-600">⏳ {state.pending}</span>
                      <span className="font-medium">Total: {state.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberAnalyticsSection;
